import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import SkipToContent from "@/components/SkipToContent";
import Navigation from "@/components/Navigation";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";

import ExternalRedirect from "@/components/ExternalRedirect";
import { hasConsent, trackPageView } from "@/lib/analytics";
import TeamAuthReturn from '@/components/TeamAuthReturn';

// Lazy-loaded page components
const Home = lazy(() => import("./pages/Home"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));
const HowWeWork = lazy(() => import("./pages/HowWeWork"));
const Work = lazy(() => import("./pages/Work"));
const CompetitorResearch = lazy(() => import("./pages/CompetitorResearch"));

// Page view tracking component
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (hasConsent()) {
      trackPageView(location.pathname);
    }
  }, [location.pathname]);

  return null;
};

// Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[calc(100vh-160px)]">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

// Strip legacy /de or /nl prefix and preserve the deep path.
const LegacyLocaleRedirect = () => {
  const location = useLocation();
  const target = location.pathname.replace(/^\/(de|nl)/, '') || '/';
  return <Navigate replace to={target + location.search + location.hash} />;
};

const App = () => (
  <>
    <PageViewTracker />
    <TeamAuthReturn />
    <ScrollToTop />
    <SkipToContent />
    <Toaster />
    <Sonner />
    <div className="sticky top-0 z-[100]">
      <AnnouncementBanner />
      <Navigation />
    </div>
    <main id="main-content">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Canonical English-only routes ───────────────── */}
          <Route path="/">
            <Route index element={<Home />} />
            {/* Retired program pages → /how-we-work (static 301 stubs emitted at build; these keep the dev preview consistent) */}
            <Route path="make" element={<Navigate replace to="/how-we-work" />} />
            <Route path="track" element={<Navigate replace to="/how-we-work" />} />
            <Route path="take" element={<Navigate replace to="/how-we-work" />} />
            <Route path="remake" element={<Navigate replace to="/how-we-work" />} />
            <Route path="readiness-briefing" element={<Navigate replace to="/how-we-work" />} />
            <Route path="faq" element={<Navigate replace to="/how-we-work" />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="how-we-work" element={<HowWeWork />} />
            <Route path="work" element={<Work />} />
            <Route path="contact" element={<Contact />} />
            <Route path="thank-you" element={<ThankYou />} />
            <Route path="one-pager" element={<Navigate replace to="/" />} />
            <Route path="qr" element={<Navigate replace to="/" />} />
            <Route path="hangtag" element={<Navigate replace to="/" />} />
            <Route path="email" element={<Navigate replace to="/" />} />
            <Route path="share" element={<Navigate replace to="/" />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="terms" element={<Terms />} />
            <Route path="marketing/competitors" element={<CompetitorResearch />} />

            <Route path="company/movement" element={<Navigate replace to="/about" />} />

            {/*
              All legacy 1:1 redirects (pricing, platform, team, solutions/*, products/*,
              company/about, /for-*, /2026-roadmap, /pages/<known-slug>) are emitted as
              static HTML stubs at build time by scripts/vite-plugin-static-seo.ts
              (see `redirectRoutes`). Kept out of the SPA router so they don't hydrate.
            */}

            {/* B2C-only legacy pages → forward to the live Shopify site */}
            <Route path="pages/*" element={<ExternalRedirect passthrough origin="https://circloloop.com" />} />
            <Route path="blogs/*" element={<ExternalRedirect passthrough origin="https://circloloop.com" />} />
            <Route path="collections/*" element={<ExternalRedirect passthrough origin="https://circloloop.com" />} />
            <Route path="products/*" element={<ExternalRedirect passthrough origin="https://circloloop.com" />} />
          </Route>

          {/* Legacy locale paths → English equivalents */}
          <Route path="/de/*" element={<LegacyLocaleRedirect />} />
          <Route path="/nl/*" element={<LegacyLocaleRedirect />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </main>
    <Footer />
    <CookieConsent />
  </>
);

export default App;
